/**
 * Shared variant option logic for PDP (variant-picker) and bundle builder.
 * Keep supplier/fabric preference, hidden-option resolution, and availability
 * checks in one place so both flows stay aligned.
 */

/** @param {unknown} name */
export function normalizeOptionName(name) {
  return String(name ?? "")
    .trim()
    .toLowerCase();
}

/** @param {unknown} optionName @param {string} type */
export function matchesOptionType(optionName, type) {
  const n = normalizeOptionName(optionName);
  const t = normalizeOptionName(type);

  if (t === "colour" || t === "color") {
    return /colou?r|färg|couleur|farbe/.test(n);
  }
  if (t === "size") {
    return /(^size$|storlek|taille|größe|talla|misura)/.test(n);
  }
  if (t === "fabric") {
    return /(^fabric$|tyg|matière|stoff)/.test(n);
  }
  if (t === "supplier") {
    return n === "supplier";
  }

  return n.includes(t);
}

/** @param {unknown} optionName */
export function isColourOption(optionName) {
  return matchesOptionType(optionName, "colour");
}

/** @param {unknown} optionName */
export function isSizeOption(optionName) {
  return matchesOptionType(optionName, "size");
}

/** @param {unknown} optionName */
export function isFabricOption(optionName) {
  return matchesOptionType(optionName, "fabric");
}

/** @param {unknown} optionName */
export function isSupplierOption(optionName) {
  return matchesOptionType(optionName, "supplier");
}

/** Supplier & variant-level Fabric — hidden in UI on PDP and bundle builder. */
export function isHiddenVariantOption(optionName) {
  return isSupplierOption(optionName) || isFabricOption(optionName);
}

/**
 * @param {string[]} optionNames
 * @param {string[]} types
 * @returns {number}
 */
export function findOptionIndexByType(optionNames, types) {
  if (!optionNames?.length) return -1;
  for (let i = 0; i < optionNames.length; i++) {
    if (types.some((type) => matchesOptionType(optionNames[i], type))) {
      return i;
    }
  }
  return -1;
}

/**
 * @param {{ variants?: Array<{ options?: Record<string, string> }> }} product
 * @param {string[]} types
 * @returns {string|null}
 */
export function findOptionNameByType(product, types) {
  if (!product?.variants?.length) return null;
  const names = Object.keys(product.variants[0].options || {});
  return names.find((name) => types.some((type) => matchesOptionType(name, type))) || null;
}

/** True when the product has hidden Supplier and/or Fabric variant options (PDP hasSupplier). */
export function productHasSupplierOrFabric(product) {
  return Boolean(findOptionNameByType(product, ["supplier"]) || findOptionNameByType(product, ["fabric"]));
}

/**
 * @param {{ optionsWithValues?: Array<{ name: string, values: string[] }>, variants?: Array }} product
 * @param {string} optionName
 * @returns {string[]}
 */
export function getOrderedOptionValues(product, optionName) {
  const fromConfig = product.optionsWithValues?.find(
    (o) => normalizeOptionName(o.name) === normalizeOptionName(optionName)
  );
  if (fromConfig?.values?.length) return fromConfig.values;

  const values = new Set();
  (product.variants || []).forEach((variant) => {
    const val = variant.options?.[optionName];
    if (val != null && val !== "") values.add(val);
  });
  return Array.from(values);
}

/**
 * @param {Array<{ options?: Record<string, string> }>} variants
 * @param {string} optionName
 * @returns {string[]}
 */
export function getUniqueOptionValuesFromVariants(variants, optionName) {
  const values = new Set();
  (variants || []).forEach((variant) => {
    const val = variant.options?.[optionName];
    if (val != null && val !== "") values.add(val);
  });
  return Array.from(values);
}

/**
 * Map a variant-picker row (option1/2/3) to { [optionName]: value }.
 * @param {{ option1?: string, option2?: string, option3?: string }} variant
 * @param {string[]} optionNames
 */
export function pickerVariantToOptionsMap(variant, optionNames) {
  /** @type {Record<string, string>} */
  const options = {};
  (optionNames || []).forEach((name, index) => {
    const value = [variant.option1, variant.option2, variant.option3][index];
    if (value != null && value !== "") options[name] = value;
  });
  return options;
}

/**
 * Adapt variant-picker JSON to the product shape used by findAvailableVariantForOptions.
 * @param {Array<{ id?: number, available?: boolean, option1?: string, option2?: string, option3?: string }>} variantData
 * @param {string[]} optionNames
 * @param {Array<{ name: string, values?: string[], position?: number }>} [optionsWithValues]
 */
export function productFromPickerVariants(variantData, optionNames, optionsWithValues = []) {
  const names = (optionNames || []).filter(Boolean);

  return {
    optionsWithValues:
      optionsWithValues.length > 0
        ? optionsWithValues
        : names.map((name, index) => ({
            name,
            values: [
              ...new Set(
                (variantData || [])
                  .map((variant) => [variant.option1, variant.option2, variant.option3][index])
                  .filter((value) => value != null && value !== "")
              )
            ]
          })),
    variants: (variantData || []).map((variant) => ({
      id: variant.id,
      available: variant.available,
      options: pickerVariantToOptionsMap(variant, names)
    }))
  };
}

/**
 * Adapt variant-picker selected options to a name-keyed map.
 * @param {Array<{ value?: string }>} pickerOptions
 * @param {string[]} optionNames
 */
export function selectedOptionsFromPicker(pickerOptions, optionNames) {
  /** @type {Record<string, string>} */
  const options = {};
  (optionNames || []).forEach((name, index) => {
    const value = pickerOptions?.[index]?.value;
    if (value != null && value !== "") options[name] = value;
  });
  return options;
}

/**
 * @param {{ options?: Record<string, string> }} variant
 * @param {Record<string, string>} options
 */
export function variantMatchesOptions(variant, options) {
  return Object.entries(options).every(([name, val]) => {
    if (val == null || val === "") return true;
    return variant.options?.[name] === val;
  });
}

/**
 * Visible selections only — strip auto-resolved supplier/fabric so availability
 * matches PDP: an option is in stock if ANY hidden supplier/fabric combo works.
 *
 * @param {{
 *   selectedOptions: Record<string, string>,
 *   optionName: string,
 *   value: string,
 *   supplierName: string|null,
 *   fabricName: string|null
 * }} params
 */
function buildVisibleOptionsForAvailability({ selectedOptions, optionName, value, supplierName, fabricName }) {
  const options = { ...selectedOptions, [optionName]: value };
  if (supplierName) delete options[supplierName];
  if (fabricName) delete options[fabricName];
  return options;
}

/**
 * @param {Array<{ available?: boolean, options?: Record<string, string> }>} variants
 * @param {Record<string, string>} options
 */
function hasAvailableVariantMatchingOptions(variants, options) {
  return variants.some((variant) => variant.available && variantMatchesOptions(variant, options));
}

/**
 * PDP-style check for size/colour: available when any hidden supplier/fabric
 * variant exists for the visible option combination.
 *
 * @param {{
 *   variants: Array,
 *   selectedOptions: Record<string, string>,
 *   optionName: string,
 *   value: string,
 *   supplierName: string|null,
 *   fabricName: string|null
 * }} params
 */
function isAvailableAcrossHiddenSupplierFabricCombos({
  variants,
  selectedOptions,
  optionName,
  value,
  supplierName,
  fabricName
}) {
  const visibleOptions = buildVisibleOptionsForAvailability({
    selectedOptions,
    optionName,
    value,
    supplierName,
    fabricName
  });
  return hasAvailableVariantMatchingOptions(variants, visibleOptions);
}

/**
 * @param {{ variants?: Array }} product
 * @param {Record<string, string>} options
 * @param {string} [optionName]
 * @param {string} [value]
 */
export function isVariantAvailableForOptions(product, options, optionName, value) {
  const test = optionName != null ? { ...options, [optionName]: value } : { ...options };
  return (product?.variants || []).some((variant) => {
    if (!variant.available) return false;
    return variantMatchesOptions(variant, test);
  });
}

/**
 * Prefer first/second fabric from admin order based on switchSupplier, with stock fallback.
 * @param {{ fabricValues: string[], switchSupplier: boolean, isAvailable: (value: string) => boolean }} params
 * @returns {string|null}
 */
export function pickPreferredFabricValue({ fabricValues, switchSupplier, isAvailable }) {
  if (!fabricValues?.length) return null;

  const secondOrLastIdx = Math.min(1, fabricValues.length - 1);
  const primaryIdx = switchSupplier ? secondOrLastIdx : 0;
  const fallbackIdx = switchSupplier ? 0 : secondOrLastIdx;
  let pick = fabricValues[primaryIdx];

  if (pick && !isAvailable(pick) && fabricValues[fallbackIdx]) {
    pick = fabricValues[fallbackIdx];
  }

  return pick || null;
}

/**
 * Prefer TCT/PFL from switchSupplier; fall back to the other supplier when sold out.
 * @param {{ hasSupplier: boolean, switchSupplier: boolean, isAvailable: (supplier: string) => boolean }} params
 * @returns {string|null}
 */
export function pickPreferredSupplierValue({ hasSupplier, switchSupplier, isAvailable }) {
  if (!hasSupplier) return null;

  const preferred = switchSupplier ? "TCT" : "PFL";
  const fallback = preferred === "TCT" ? "PFL" : "TCT";

  if (isAvailable(preferred)) return preferred;
  if (isAvailable(fallback)) return fallback;
  return preferred;
}

/**
 * Apply hidden supplier/fabric defaults to a copy (no state mutation).
 * Same sequence as variant-picker onVariantChange: fabric → supplier → fabric.
 *
 * @param {{
 *   product: { variants?: Array, optionsWithValues?: Array },
 *   options: Record<string, string>,
 *   hasSupplier: boolean,
 *   switchSupplier: boolean,
 *   touchedOptions?: Record<string, boolean>
 * }} params
 * @returns {Record<string, string>}
 */
export function resolveHiddenOptionsOnCopy({ product, options, hasSupplier, switchSupplier, touchedOptions = {} }) {
  const nextOptions = { ...options };
  if (!product) return nextOptions;

  const supplierName = findOptionNameByType(product, ["supplier"]);
  const fabricName = findOptionNameByType(product, ["fabric"]);

  const applyFabric = () => {
    if (!fabricName || touchedOptions[fabricName]) return;
    const fabricValues = getOrderedOptionValues(product, fabricName);
    const pick = pickPreferredFabricValue({
      fabricValues,
      switchSupplier,
      isAvailable: (value) => isVariantAvailableForOptions(product, nextOptions, fabricName, value)
    });
    if (pick) nextOptions[fabricName] = pick;
  };

  const applySupplier = () => {
    if (!supplierName || !hasSupplier || touchedOptions[supplierName]) return;
    const pick = pickPreferredSupplierValue({
      hasSupplier,
      switchSupplier,
      isAvailable: (value) => isVariantAvailableForOptions(product, nextOptions, supplierName, value)
    });
    if (pick) nextOptions[supplierName] = pick;
  };

  applyFabric();
  applySupplier();
  applyFabric();
  return nextOptions;
}

/**
 * Resolve an in-stock variant for the current selections.
 * Tries preferred hidden supplier/fabric first, then any in-stock hidden combo.
 *
 * @param {{
 *   product: { variants?: Array, optionsWithValues?: Array },
 *   options: Record<string, string>,
 *   hasSupplier: boolean,
 *   switchSupplier: boolean,
 *   touchedOptions?: Record<string, boolean>
 * }} params
 */
export function findAvailableVariantForOptions({ product, options, hasSupplier, switchSupplier, touchedOptions = {} }) {
  const variants = product?.variants || [];
  const baseOptions = { ...options };
  if (!product || !variants.length) return { variant: null, options: baseOptions };

  const pickAvailable = (candidateOptions) =>
    variants.find((variant) => variant.available && variantMatchesOptions(variant, candidateOptions)) || null;

  const resolved = resolveHiddenOptionsOnCopy({
    product,
    options: baseOptions,
    hasSupplier,
    switchSupplier,
    touchedOptions
  });

  let variant = pickAvailable(resolved);
  if (variant) return { variant, options: resolved };

  const supplierName = findOptionNameByType(product, ["supplier"]);
  const fabricName = findOptionNameByType(product, ["fabric"]);
  if (!supplierName && !fabricName) {
    return { variant: null, options: resolved };
  }

  const visibleOptions = { ...resolved };
  if (supplierName && !touchedOptions[supplierName]) delete visibleOptions[supplierName];
  if (fabricName && !touchedOptions[fabricName]) delete visibleOptions[fabricName];

  const fabricValues =
    fabricName && !touchedOptions[fabricName]
      ? getUniqueOptionValuesFromVariants(variants, fabricName)
      : [resolved[fabricName] ?? null];
  const supplierValues =
    supplierName && !touchedOptions[supplierName] && hasSupplier
      ? getUniqueOptionValuesFromVariants(variants, supplierName)
      : [resolved[supplierName] ?? null];

  for (const fabricValue of fabricValues) {
    for (const supplierValue of supplierValues) {
      const combo = { ...visibleOptions };
      if (fabricValue != null && fabricName) combo[fabricName] = fabricValue;
      if (supplierValue != null && supplierName) combo[supplierName] = supplierValue;
      variant = pickAvailable(combo);
      if (variant) return { variant, options: combo };
    }
  }

  return { variant: null, options: resolved };
}

/**
 * Check if an option value has an in-stock variant, mirroring variant-picker
 * isOptionAvailableAndInStock for the bundle-builder's options map shape.
 *
 * @param {{
 *   product: { variants?: Array, optionsWithValues?: Array },
 *   selectedOptions: Record<string, string>,
 *   optionName: string,
 *   value: string,
 *   hasSupplier: boolean,
 *   switchSupplier: boolean,
 *   touchedOptions?: Record<string, boolean>
 * }} params
 */
export function isOptionValueAvailableForProduct({
  product,
  selectedOptions,
  optionName,
  value,
  hasSupplier,
  switchSupplier,
  touchedOptions = {}
}) {
  const variants = product?.variants || [];
  if (!variants.length) return false;

  const testOptions = { ...selectedOptions, [optionName]: value };
  const supplierName = findOptionNameByType(product, ["supplier"]);
  const fabricName = findOptionNameByType(product, ["fabric"]);
  const hasHiddenSupplierOrFabric = Boolean(supplierName || fabricName);

  // Size: available if any hidden supplier/fabric variant matches (PDP behaviour).
  if (isSizeOption(optionName) && hasHiddenSupplierOrFabric) {
    return isAvailableAcrossHiddenSupplierFabricCombos({
      variants,
      selectedOptions,
      optionName,
      value,
      supplierName,
      fabricName
    });
  }

  // Colour: require selected size; available if any hidden supplier/fabric variant matches.
  if (isColourOption(optionName)) {
    const sizeName = findOptionNameByType(product, ["size"]);
    const selectedSize = sizeName ? selectedOptions[sizeName] : null;

    if (sizeName && (!selectedSize || selectedSize === "")) {
      return false;
    }

    if (hasHiddenSupplierOrFabric) {
      return isAvailableAcrossHiddenSupplierFabricCombos({
        variants,
        selectedOptions,
        optionName,
        value,
        supplierName,
        fabricName
      });
    }

    return hasAvailableVariantMatchingOptions(variants, testOptions);
  }

  const resolved = resolveHiddenOptionsOnCopy({
    product,
    options: testOptions,
    hasSupplier,
    switchSupplier,
    touchedOptions
  });

  return variants.some((variant) => variant.available && variantMatchesOptions(variant, resolved));
}
